import React, { useRef } from "react";
import { ZodSchema, z } from "zod";

import { faXmark } from "@performyard/fontawesome/pro-solid-svg-icons";

import { ButtonIcon } from "../../atoms/ButtonIcon";
import { Text } from "../../atoms/Text";
import { PanelBase } from "../../molecules/PanelBase";
import { FormHandle, PYForm, SubmitButton } from "../Form";

export type PanelFiltersProps<TSchema extends ZodSchema> = {
	/**
	 * React node content to display inside the panel (form filter inputs)
	 */
	children: React.ReactNode;
	/**
	 * Whether the panel is open or closed
	 */
	isOpen: boolean;
	/**
	 * Callback function executed when the panel should be closed
	 */
	onClose: () => void;
	/**
	 * Zod schema for form validation
	 */
	schema: TSchema;
	/**
	 * Default values for the form fields
	 */
	defaultValues: z.infer<TSchema>;
	/**
	 * Callback function executed when form is submitted
	 */
	onSubmit: ({
		value,
		resetForm,
		setError,
	}: {
		value: z.infer<TSchema>;
		resetForm: () => void;
		setError: (field: keyof z.infer<TSchema>, error: string) => void;
	}) => void | Promise<void>;
};

/**
 * PanelFilters component that provides a standardized filter panel template with integrated form validation.
 *
 * This component wraps PanelBase and PYForm to provide a consistent structure for filter panels
 * with automatic form validation, type-safe inputs, and built-in error handling.
 *
 * ## Features
 * - **Form Integration**: Built-in PYForm with Zod validation
 * - **Type Safety**: Full TypeScript support with type-safe form fields
 * - **Consistent Structure**: Pre-configured header with title and close button
 * - **Scrollable Content**: Content area automatically scrolls when needed
 * - **Sticky Footer**: "Apply Filters" submit button that stays at the bottom
 * - **Portal rendering**: Inherits portal rendering from PanelBase for proper layering
 * - **Accessibility**: Proper ARIA attributes and focus management
 *
 * @example
 * ```tsx
 * const filterSchema = z.object({
 *   search: z.string(),
 *   status: z.array(z.string()),
 *   dateRange: z.object({
 *     from: z.date().optional(),
 *     to: z.date().optional()
 *   }).optional()
 * });
 *
 * <PanelFilters
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   schema={filterSchema}
 *   defaultValues={{ search: "", status: [], dateRange: undefined }}
 *   onSubmit={({ value, resetForm }) => {
 *     applyFilters(value);
 *     handleClose();
 *   }}
 * >
 *   <FormInputText id="search" label="Search" />
 *   <FormCheckboxGroup id="status" label="Status" options={statusOptions} />
 *   <FormDateRangePicker id="dateRange" label="Date Range" />
 * </PanelFilters>
 * ```
 */
const PanelFilters = <TSchema extends ZodSchema>(props: PanelFiltersProps<TSchema>) => {
	const { children, isOpen, onClose, schema, defaultValues, onSubmit } = props;
	const formRef = useRef<FormHandle<TSchema>>(null);

	return (
		<PanelBase isOpen={isOpen} onClose={onClose} side="left">
			<PYForm
				ref={formRef}
				id="panel-filters-form"
				schema={schema}
				defaultValues={defaultValues}
				onSubmit={onSubmit}
				validateOn={["onSubmit"]}
				dangerouslySetClassName="flex h-full flex-col"
			>
				<div className="flex-1 overflow-y-auto px-6 pt-6">
					<div className="flex items-center justify-between pb-6">
						<Text size="sub-heading" weight="semi-bold">
							Apply Filters
						</Text>
						<ButtonIcon
							size={16}
							icon={faXmark}
							onClick={onClose}
							variant="ghost"
							dangerouslySetClassName="p-4"
						/>
					</div>
					{children}
				</div>
				<div className="p-6">
					<SubmitButton dangerouslySetClassName="w-full">Apply Filters</SubmitButton>
				</div>
			</PYForm>
		</PanelBase>
	);
};

PanelFilters.displayName = "PanelFilters";

export { PanelFilters };
